import React, { useState, useEffect } from 'react';
import { Store, Package, Zap, DollarSign, Plus, TrendingUp, BarChart3, Brain, AlertTriangle, Sparkles, Users, Crown, CheckCircle, Truck, Wallet, Banknote, Film, Loader2, Clapperboard } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { productsAPI, flashDealsAPI, analyticsAPI, aiAPI, ordersAPI, vendorAPI, bookingsAPI, deliveryAPI, walletAPI, videoAPI, storyAPI } from '../lib/api';
import GettingStarted from '../components/guidance/GettingStarted';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const CATEGORIES = ['Electronics', 'Fashion', 'Home', 'Sports', 'Beauty', 'Food'];
const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const PLAN_COLORS = { starter: 'border-zinc-300', growth: 'border-blue-400', pro: 'border-violet-500', enterprise: 'border-amber-400' };

export default function VendorDashboard() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [vendorBookings, setVendorBookings] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [productForm, setProductForm] = useState({ name: '', description: '', price: '', category: '', inventory: '100', image_url: '' });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [dealForm, setDealForm] = useState({ product_id: '', discount_percent: '20', duration_minutes: '30', max_claims: '50' });
  const [creatingDeal, setCreatingDeal] = useState(false);

  const [aiCopy, setAiCopy] = useState(null);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [inventoryInsights, setInventoryInsights] = useState([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [pricingSuggestion, setPricingSuggestion] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [creatingAIDeal, setCreatingAIDeal] = useState(null);
  const [generatingVideoFor, setGeneratingVideoFor] = useState(null);
  const [generatingStoryFor, setGeneratingStoryFor] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, aRes, oRes, bRes, sRes, plRes, wRes, tRes, wdRes] = await Promise.all([
          productsAPI.getVendorProducts(),
          analyticsAPI.getVendorAnalytics(),
          ordersAPI.getVendorOrders().catch(() => ({ data: [] })),
          bookingsAPI.getVendorBookings().catch(() => ({ data: [] })),
          vendorAPI.getSubscription().catch(() => ({ data: { plan: 'starter' } })),
          vendorAPI.getPlans().catch(() => ({ data: {} })),
          walletAPI.getBalance().catch(() => ({ data: { balance: 0 } })),
          walletAPI.getTransactions().catch(() => ({ data: [] })),
          walletAPI.getWithdrawals().catch(() => ({ data: [] })),
        ]);
        setProducts(pRes.data);
        setAnalytics(aRes.data);
        setVendorOrders(oRes.data);
        setVendorBookings(bRes.data);
        setSubscription(sRes.data);
        setPlans(plRes.data);
        setWalletBalance(wRes.data);
        setTransactions(tRes.data);
        setWithdrawals(wdRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleCreateProduct = async () => {
    if (!productForm.name || !productForm.price || !productForm.category) { toast.error('Fill required fields'); return; }
    setCreatingProduct(true);
    try {
      const res = await productsAPI.create({ ...productForm, price: parseFloat(productForm.price), inventory: parseInt(productForm.inventory) });
      setProducts([res.data, ...products]);
      setProductForm({ name: '', description: '', price: '', category: '', inventory: '100', image_url: '' });
      toast.success('Product created!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreatingProduct(false); }
  };

  const handleCreateFlashDeal = async () => {
    if (!dealForm.product_id) { toast.error('Select a product'); return; }
    setCreatingDeal(true);
    try {
      await flashDealsAPI.create({ product_id: dealForm.product_id, discount_percent: parseFloat(dealForm.discount_percent), duration_minutes: parseInt(dealForm.duration_minutes), max_claims: parseInt(dealForm.max_claims) });
      toast.success('Flash deal is live!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreatingDeal(false); }
  };

  const handleAICreateDeal = async (product) => {
    setCreatingAIDeal(product.id);
    try {
      const res = await flashDealsAPI.aiCreate(product.id);
      toast.success(`AI deal created: ${res.data.ai_reasoning || 'Live!'}`);
    } catch (e) { toast.error('Failed'); }
    finally { setCreatingAIDeal(null); }
  };

  const handleGenerateProductVideo = async (product) => {
    setGeneratingVideoFor(product.id);
    try {
      const res = await videoAPI.generate({ product_id: product.id, duration: 12 });
      if (res.data.status === 'completed') {
        toast.success('Video already exists for this product!');
      } else {
        toast.info(`Sora 2 is generating a video for "${product.name}". This takes 2-5 min.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start video generation');
    } finally {
      setGeneratingVideoFor(null);
    }
  };

  const handleGenerateStoryVideo = async (product) => {
    setGeneratingStoryFor(product.id);
    try {
      const res = await storyAPI.generate({ product_id: product.id, scene_count: 5 });
      if (res.data.status === 'completed') {
        toast.success('Story video already exists!');
      } else {
        toast.info(`Producing 5-scene story for "${product.name}" with AI narration. ~12 min.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start story generation');
    } finally {
      setGeneratingStoryFor(null);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      setVendorOrders(vendorOrders.map(o => o.id === orderId ? { ...o, status } : o));
      toast.success(`Order ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSubscribe = async (plan) => {
    try {
      await vendorAPI.subscribe(plan);
      const sRes = await vendorAPI.getSubscription();
      setSubscription(sRes.data);
      toast.success(`Subscribed to ${plan} plan!`);
    } catch (e) { toast.error('Failed'); }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) return;
    setIsWithdrawing(true);
    try {
      await walletAPI.withdraw(amount);
      toast.success(`Withdrawal of $${amount} is processing`);
      const [wRes, tRes, wdRes] = await Promise.all([walletAPI.getBalance(), walletAPI.getTransactions(), walletAPI.getWithdrawals()]);
      setWalletBalance(wRes.data);
      setTransactions(tRes.data);
      setWithdrawals(wdRes.data);
      setWithdrawAmount('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Withdrawal failed'); }
    finally { setIsWithdrawing(false); }
  };

  // Chart data
  const categoryData = products.reduce((acc, p) => {
    const existing = acc.find(c => c.name === p.category);
    if (existing) existing.count++;
    else acc.push({ name: p.category, count: 1 });
    return acc;
  }, []);

  const revenueData = vendorOrders.filter(o => o.status === 'paid' || o.status === 'delivered').slice(0, 10).map((o, i) => ({
    name: `#${i + 1}`, amount: o.total_price
  }));

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen pb-8">
      <GettingStarted pageContext="vendor" />
      <section className="px-4 py-8 border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-violet-100 border border-violet-200 rounded-xl"><Store className="text-violet-600" size={24} /></div>
              <div>
                <h1 className="font-display font-bold text-2xl">Vendor Dashboard</h1>
                <p className="text-zinc-500 text-sm">Welcome, {user?.name} <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-600 rounded-full ml-1">{subscription?.plan || 'starter'} plan</span></p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Products', value: analytics?.total_products || 0, icon: Package, color: 'bg-zinc-50 border-zinc-200' },
              { label: 'Active Deals', value: analytics?.active_flash_deals || 0, icon: Zap, color: 'bg-green-50 border-green-200', textColor: 'text-green-700' },
              { label: 'Orders', value: analytics?.total_orders || 0, icon: TrendingUp, color: 'bg-violet-50 border-violet-200', textColor: 'text-violet-700' },
              { label: 'Revenue', value: formatCurrency(analytics?.total_revenue || 0), icon: DollarSign, color: 'bg-emerald-50 border-emerald-200', textColor: 'text-emerald-600' },
              { label: 'Wallet', value: formatCurrency(walletBalance?.balance || 0), icon: Wallet, color: 'bg-amber-50 border-amber-200', textColor: 'text-amber-600' },
            ].map(({ label, value, icon: Icon, color, textColor }) => (
              <div key={label} className={`p-4 border rounded-xl ${color}`}>
                <Icon className="mb-2 text-zinc-400" size={20} />
                <p className={`font-display font-bold text-2xl ${textColor || 'text-zinc-900'}`}>{value}</p>
                <p className="text-xs text-zinc-500">{label}</p>
              </div>
            ))}
          </div>

          {analytics?.ai_insights?.length > 0 && (
            <div className="mt-4 space-y-2">
              {analytics.ai_insights.map((insight, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${insight.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <AlertTriangle size={16} className={insight.severity === 'high' ? 'text-red-500' : 'text-amber-500'} />
                  <span className="text-sm text-zinc-700">{insight.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px-4 py-6 max-w-7xl mx-auto">
        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="bg-zinc-100 border border-zinc-200 mb-6 flex-wrap">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white"><BarChart3 size={14} className="mr-1" /> Analytics</TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-white">Products</TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-white">Orders</TabsTrigger>
            <TabsTrigger value="flash-deals" className="data-[state=active]:bg-white">Flash Deals</TabsTrigger>
            <TabsTrigger value="ai-tools" className="data-[state=active]:bg-white"><Brain size={14} className="mr-1" /> AI Tools</TabsTrigger>
            <TabsTrigger value="earnings" className="data-[state=active]:bg-white"><Wallet size={14} className="mr-1" /> Earnings</TabsTrigger>
            <TabsTrigger value="subscription" className="data-[state=active]:bg-white"><Crown size={14} className="mr-1" /> Plans</TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Distribution */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                <h3 className="font-display font-semibold mb-4">Product Categories</h3>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="count" label={({ name, count }) => `${name} (${count})`}>
                        {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-zinc-400 text-center py-10">No products yet</p>}
              </div>

              {/* Revenue Chart */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                <h3 className="font-display font-semibold mb-4">Revenue Overview</h3>
                {revenueData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Area type="monotone" dataKey="amount" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <p className="text-zinc-400 text-center py-10">No revenue data yet</p>}
              </div>

              {/* Top Products */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 md:col-span-2">
                <h3 className="font-display font-semibold mb-4">Top Products by Rating</h3>
                {analytics?.top_products?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={analytics.top_products.slice(0, 5).map(p => ({ name: p.name.slice(0, 15), rating: p.rating, reviews: p.reviews_count }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="rating" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="reviews" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-zinc-400 text-center py-10">No product data</p>}
              </div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-semibold text-lg">Your Products ({products.length})</h2>
              <Dialog>
                <DialogTrigger asChild><Button className="bg-violet-600 text-white" data-testid="add-product-btn"><Plus size={16} className="mr-1" /> Add Product</Button></DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Add New Product</DialogTitle></DialogHeader>
                  <div className="space-y-3 pt-2">
                    <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="Product name *" data-testid="product-name-input" />
                    <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} placeholder="Description" />
                    <div className="grid grid-cols-2 gap-3">
                      <Input type="number" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} placeholder="Price *" />
                      <Input type="number" value={productForm.inventory} onChange={(e) => setProductForm({ ...productForm, inventory: e.target.value })} placeholder="Inventory" />
                    </div>
                    <Select value={productForm.category} onValueChange={(v) => setProductForm({ ...productForm, category: v })}>
                      <SelectTrigger><SelectValue placeholder="Category *" /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={handleCreateProduct} disabled={creatingProduct} className="w-full bg-violet-600 text-white" data-testid="create-product-btn">{creatingProduct ? 'Creating...' : 'Create Product'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-3">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 bg-white border border-zinc-200 rounded-xl">
                  <div className="w-14 h-14 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover rounded-lg" /> : <Package className="text-zinc-400" size={20} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{p.name}</h3>
                    <p className="text-sm text-zinc-500">{p.category} · {p.inventory} stock · {p.rating > 0 ? `${p.rating}★` : 'No reviews'}</p>
                    <p className="font-display font-bold text-green-600">{formatCurrency(p.price)}</p>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => handleGenerateProductVideo(p)} disabled={generatingVideoFor === p.id} className="text-xs border-violet-300 text-violet-600">
                      {generatingVideoFor === p.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Film size={12} className="mr-1" />} {generatingVideoFor === p.id ? '...' : 'AI Video'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleGenerateStoryVideo(p)} disabled={generatingStoryFor === p.id} className="text-xs border-pink-300 text-pink-600">
                      {generatingStoryFor === p.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Clapperboard size={12} className="mr-1" />} {generatingStoryFor === p.id ? '...' : 'Story'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleAICreateDeal(p)} disabled={creatingAIDeal === p.id} className="text-xs border-green-300 text-green-600">
                      <Zap size={12} className="mr-1" /> {creatingAIDeal === p.id ? '...' : 'AI Deal'}
                    </Button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <div className="text-center py-12 bg-zinc-50 rounded-xl"><Package className="mx-auto mb-3 text-zinc-400" size={40} /><p className="text-zinc-500">No products yet</p></div>}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <h2 className="font-display font-semibold text-lg mb-4">Customer Orders ({vendorOrders.length})</h2>
            <div className="space-y-3">
              {vendorOrders.map((order) => (
                <div key={order.id} className="bg-white border border-zinc-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm">{order.product_name || order.service_name}</p>
                      <p className="text-xs text-zinc-500">By: {order.buyer_name} · {order.buyer_email}</p>
                    </div>
                    <p className="font-display font-bold text-green-600">{formatCurrency(order.total_price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${order.status === 'delivered' ? 'bg-green-100 text-green-700' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{order.status}</span>
                    {['pending_payment', 'confirmed'].includes(order.status) && (
                      <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'processing')} className="text-xs">Process</Button>
                    )}
                    {order.status === 'processing' && (
                      <Button size="sm" variant="outline" onClick={async () => {
                        try {
                          await deliveryAPI.ship(order.id);
                          setVendorOrders(vendorOrders.map(o => o.id === order.id ? { ...o, status: 'shipped' } : o));
                          toast.success('Shipped via OVA Rideshare!');
                        } catch (e) {
                          await handleUpdateOrderStatus(order.id, 'shipped');
                        }
                      }} className="text-xs"><Truck size={12} className="mr-1" /> Ship via OVA</Button>
                    )}
                    {order.status === 'shipped' && (
                      <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'delivered')} className="text-xs text-green-600">Deliver</Button>
                    )}
                  </div>
                </div>
              ))}
              {vendorOrders.length === 0 && <p className="text-zinc-400 text-center py-12">No orders yet</p>}
            </div>
          </TabsContent>

          {/* Flash Deals Tab */}
          <TabsContent value="flash-deals">
            <div className="p-6 bg-white border border-zinc-200 rounded-xl">
              <h2 className="font-display font-semibold text-lg mb-4">Create Flash Deal</h2>
              <div className="space-y-4">
                <Select value={dealForm.product_id} onValueChange={(v) => setDealForm({ ...dealForm, product_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose a product" /></SelectTrigger>
                  <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}</SelectItem>)}</SelectContent>
                </Select>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Discount %</Label><Input type="number" value={dealForm.discount_percent} onChange={(e) => setDealForm({ ...dealForm, discount_percent: e.target.value })} /></div>
                  <div><Label>Duration (min)</Label><Input type="number" value={dealForm.duration_minutes} onChange={(e) => setDealForm({ ...dealForm, duration_minutes: e.target.value })} /></div>
                  <div><Label>Max Claims</Label><Input type="number" value={dealForm.max_claims} onChange={(e) => setDealForm({ ...dealForm, max_claims: e.target.value })} /></div>
                </div>
                <Button onClick={handleCreateFlashDeal} disabled={creatingDeal || !products.length} className="w-full bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black font-bold">
                  <Zap size={16} className="mr-2" /> {creatingDeal ? 'Creating...' : 'Launch Flash Deal'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* AI Tools Tab */}
          <TabsContent value="ai-tools">
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-violet-600 rounded-lg"><Brain className="text-white" size={20} /></div>
                  <div><h3 className="font-display font-bold text-lg">AI Inventory Intelligence</h3><p className="text-sm text-zinc-500">Detect slow-moving stock and get recommendations</p></div>
                </div>
                <Button onClick={async () => { setLoadingInsights(true); try { const r = await aiAPI.inventoryIntelligence(); setInventoryInsights(r.data.insights || []); } catch(e){} finally { setLoadingInsights(false); } }} disabled={loadingInsights || !products.length} className="bg-violet-600 text-white" data-testid="run-inventory-ai-btn">
                  <Brain size={16} className="mr-2" /> {loadingInsights ? 'Analyzing...' : 'Run AI Analysis'}
                </Button>
                {inventoryInsights.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {inventoryInsights.map((i, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${i.risk_level === 'high' ? 'bg-red-50 border-red-200' : i.risk_level === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                        <div className="flex gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${i.risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{i.risk_level?.toUpperCase()}</span>
                          <span className="text-xs px-2 py-0.5 bg-zinc-100 rounded-full">{i.action}</span>
                        </div>
                        <p className="text-sm text-zinc-700">{i.reasoning}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center gap-3"><div className="p-2 bg-blue-600 rounded-lg"><DollarSign className="text-white" size={20} /></div><div><h3 className="font-display font-bold text-lg">AI Dynamic Pricing</h3><p className="text-sm text-zinc-500">Click "AI Deal" on products to get AI-optimized pricing</p></div></div>
              </div>
            </div>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            <div className="space-y-6">
              {/* Wallet Balance Card */}
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-6 text-white">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-zinc-400 text-sm mb-1">Available Earnings</p>
                    <p className="font-display font-bold text-4xl" data-testid="vendor-wallet-balance" style={{ color: '#b8ff00' }}>
                      {formatCurrency(walletBalance?.balance || 0)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="bg-[#b8ff00] text-black hover:bg-[#a8ef00] rounded-full" data-testid="vendor-withdraw-btn">
                          <Banknote size={18} className="mr-1" /> Withdraw
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white border-zinc-200 rounded-2xl">
                        <DialogHeader>
                          <DialogTitle className="font-display text-zinc-900">Withdraw Earnings</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <p className="text-sm text-zinc-500">Available: <strong className="text-zinc-900">{formatCurrency(walletBalance?.balance || 0)}</strong></p>
                          <Input type="number" placeholder="Enter withdrawal amount"
                            value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)}
                            className="h-12 bg-zinc-50 border-zinc-200 rounded-xl" data-testid="vendor-withdraw-input" />
                          <Button onClick={handleWithdraw} disabled={isWithdrawing || !withdrawAmount || parseFloat(withdrawAmount) > (walletBalance?.balance || 0)}
                            className="w-full h-12 bg-zinc-900 text-white font-bold rounded-xl" data-testid="vendor-confirm-withdraw-btn">
                            {isWithdrawing ? 'Processing...' : 'Request Withdrawal'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <p className="text-xs text-zinc-400">Total Revenue</p>
                    <p className="font-display font-bold text-xl">{formatCurrency(analytics?.total_revenue || 0)}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                    <p className="text-xs text-zinc-400">Paid Orders</p>
                    <p className="font-display font-bold text-xl">{vendorOrders.filter(o => ['paid', 'shipped', 'delivered'].includes(o.status)).length}</p>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div>
                <h3 className="font-display font-semibold mb-3">Recent Transactions</h3>
                {transactions.length > 0 ? (
                  <div className="bg-white rounded-2xl border border-zinc-200 divide-y divide-zinc-100">
                    {transactions.slice(0, 15).map((tx) => (
                      <div key={tx.id} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                            {tx.type === 'credit' ? <DollarSign className="text-green-600" size={14} /> : <Banknote className="text-red-600" size={14} />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-zinc-900">{tx.description}</p>
                            <p className="text-xs text-zinc-500">{new Date(tx.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <span className={`font-mono font-bold text-sm ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-white rounded-2xl border border-zinc-200">
                    <Wallet className="mx-auto mb-2 text-zinc-300" size={32} />
                    <p className="text-zinc-500 text-sm">No earnings yet. Sales revenue will appear here.</p>
                  </div>
                )}
              </div>

              {/* Withdrawals */}
              {withdrawals.length > 0 && (
                <div>
                  <h3 className="font-display font-semibold mb-3">Withdrawal History</h3>
                  <div className="bg-white rounded-2xl border border-zinc-200 divide-y divide-zinc-100">
                    {withdrawals.map((w) => (
                      <div key={w.id} className="flex items-center justify-between p-4">
                        <div>
                          <p className="text-sm font-medium text-zinc-900">Withdrawal #{w.id.slice(0, 8)}</p>
                          <p className="text-xs text-zinc-500">{new Date(w.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-mono font-bold text-orange-600">{formatCurrency(w.amount)}</span>
                          <span className={`block text-xs px-2 py-0.5 rounded-full font-medium mt-1 ${w.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{w.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <h2 className="font-display font-semibold text-lg mb-4">Vendor Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans && Object.entries(plans).map(([name, plan]) => (
                <div key={name} className={`bg-white rounded-2xl border-2 p-6 ${subscription?.plan === name ? 'border-violet-500 ring-2 ring-violet-500/20' : PLAN_COLORS[name] || 'border-zinc-200'}`}>
                  {subscription?.plan === name && <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-medium mb-3 inline-block">Current Plan</span>}
                  <h3 className="font-display font-bold text-xl capitalize mb-1">{name}</h3>
                  <p className="font-display font-bold text-3xl mb-4">{plan.price === 0 ? 'Free' : `$${plan.price}`}<span className="text-sm font-normal text-zinc-500">/mo</span></p>
                  <ul className="space-y-2 mb-6">
                    <li className="text-sm text-zinc-600">Up to {plan.product_limit === -1 ? 'Unlimited' : plan.product_limit} products</li>
                    <li className="text-sm text-zinc-600">{(plan.commission_rate * 100).toFixed(0)}% commission</li>
                    {plan.features.map((f, i) => <li key={i} className="text-sm text-zinc-600 flex items-center gap-1"><CheckCircle size={12} className="text-green-500" />{f}</li>)}
                  </ul>
                  <Button onClick={() => handleSubscribe(name)} disabled={subscription?.plan === name} className={`w-full ${subscription?.plan === name ? 'bg-zinc-100 text-zinc-400' : 'bg-violet-600 text-white hover:bg-violet-700'}`} data-testid={`subscribe-${name}-btn`}>
                    {subscription?.plan === name ? 'Active' : 'Subscribe'}
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
